import type { JsonObject } from '../../types/json';
import type { UserPreferences } from '@src/dto/UserPreferences';
import { delay } from 'es-toolkit/promise';
import { getErrorMessage } from '../errorHandling';
import { captureAndReportError } from '../errorReporting';
import {
  normalizeMetricKey,
  trackCount,
  trackDuration,
} from '../sentryMetrics';
import { formatBytes } from '../stringHelpers';
import { getMimeTypeExtension } from './mimeTypes';
import { isRetryableUploadError } from './isRetryableUploadError';
import type {
  UploadOptions,
  UploadProgress,
  UploadProvider,
  UploadResult,
} from './types';

const UPLOAD_OPERATION_COUNT_METRIC = 'portal.web.upload.operation';
const UPLOAD_OPERATION_DURATION_METRIC = 'portal.web.upload.operation.duration';

export abstract class BaseProvider implements UploadProvider {
  abstract name: UserPreferences['uploadProvider'];

  protected maxRetries = 3;
  protected retryDelayMs = 1000;

  abstract initialize(): Promise<void>;
  abstract uploadFromBuffer(
    buffer: Buffer,
    options?: UploadOptions,
  ): Promise<UploadResult>;

  protected trackUploadOperationMetric(options: {
    operation: string;
    outcome: 'success' | 'retry' | 'failure';
    durationMs?: number;
    attempt?: number;
    retryable?: boolean;
    error?: unknown;
  }): void {
    const attributes: Record<string, string | number | boolean> = {
      provider: normalizeMetricKey(this.name),
      operation: normalizeMetricKey(options.operation),
      outcome: options.outcome,
    };

    if (options.attempt !== undefined) {
      attributes.attempt = options.attempt;
    }

    if (options.retryable !== undefined) {
      attributes.retryable = options.retryable;
    }

    if (options.error) {
      attributes.error_name = normalizeMetricKey(
        options.error instanceof Error ? options.error.name : 'unknown',
      );
    }

    trackCount(UPLOAD_OPERATION_COUNT_METRIC, 1, attributes);

    if (options.durationMs !== undefined) {
      trackDuration(UPLOAD_OPERATION_DURATION_METRIC, options.durationMs, {
        ...attributes,
        outcome: options.outcome === 'retry' ? 'failure' : options.outcome,
      });
    }
  }

  protected isRetryableError(error: unknown): boolean {
    return isRetryableUploadError(error);
  }

  protected async withRetry<T>(
    operation: () => Promise<T>,
    operationName: string,
    customMaxRetries?: number,
  ): Promise<T> {
    const maxAttempts = customMaxRetries || this.maxRetries;
    return this.withExponentialRetry(operation, {
      maxAttempts,
      baseDelayMs: this.retryDelayMs,
      maxDelayMs: 30000,
      operationName,
      mode: 'legacy',
    });
  }

  protected async retryWithExponentialBackoff<T>(
    operation: () => Promise<T>,
    options: {
      maxRetries?: number;
      baseDelay?: number;
      maxDelay?: number;
      operationName?: string;
      onRetry?: (attempt: number, error: Error) => void;
    } = {},
  ): Promise<T> {
    const {
      maxRetries = 3,
      baseDelay = 1000,
      maxDelay = 30000,
      operationName = 'unknown',
      onRetry,
    } = options;

    return this.withExponentialRetry(operation, {
      maxAttempts: maxRetries,
      baseDelayMs: baseDelay,
      maxDelayMs: maxDelay,
      operationName,
      mode: 'normalized',
      onRetry,
    });
  }

  protected getErrorMessage(error: unknown): string {
    return getErrorMessage(error);
  }

  protected getExtensionFromContentType(
    contentType?: string | null,
    options: {
      leadingDot?: boolean;
      fallback?: string;
    } = {},
  ): string {
    const extension = getMimeTypeExtension(contentType);
    if (!extension) {
      return options.fallback ?? '';
    }

    return options.leadingDot ? `.${extension}` : extension;
  }

  protected reportProgress(
    uploadedBytes: number,
    totalBytes: number,
    onProgress?: (progress: UploadProgress) => void,
    additionalInfo?: JsonObject,
  ): void {
    if (!onProgress) return;

    const percentage = Math.round((uploadedBytes / totalBytes) * 100);
    onProgress({
      percentage,
      uploadedBytes,
      totalBytes,
      message: `Uploading: ${formatBytes(uploadedBytes)}/${formatBytes(totalBytes)}`,
      ...additionalInfo,
    });
  }

  private async withExponentialRetry<T>(
    operation: () => Promise<T>,
    options: {
      maxAttempts: number;
      baseDelayMs: number;
      maxDelayMs: number;
      operationName: string;
      mode: 'legacy' | 'normalized';
      onRetry?: (attempt: number, error: Error) => void;
    },
  ): Promise<T> {
    const startedAt = Date.now();
    let lastError: Error | undefined;
    const isLegacy = options.mode === 'legacy';

    for (let attempt = 1; attempt <= options.maxAttempts; attempt++) {
      try {
        if (isLegacy) {
          console.log(
            `${options.operationName} attempt ${attempt}/${options.maxAttempts}`,
          );
        }
        const result = await operation();
        this.trackUploadOperationMetric({
          operation: options.operationName,
          outcome: 'success',
          durationMs: Date.now() - startedAt,
          attempt,
        });
        return result;
      } catch (error) {
        const normalizedError =
          error instanceof Error
            ? error
            : new Error(isLegacy ? String(error) : 'Unknown error');
        lastError = normalizedError;
        if (isLegacy) {
          captureAndReportError(
            `${options.operationName} attempt ${attempt} failed:`,
            error,
          );
        }

        const reportedError = isLegacy ? error : normalizedError;
        const retryable = this.isRetryableError(reportedError);
        const isTerminalFailure = !retryable || attempt === options.maxAttempts;

        this.trackUploadOperationMetric({
          operation: options.operationName,
          outcome: isTerminalFailure ? 'failure' : 'retry',
          attempt,
          retryable,
          error: reportedError,
          durationMs: isTerminalFailure ? Date.now() - startedAt : undefined,
        });

        if (isTerminalFailure) {
          throw reportedError;
        }

        const delayWithoutJitter =
          options.baseDelayMs * 2 ** (attempt - (isLegacy ? 0 : 1));
        const jitter = Math.random() * 1000;
        const delayMs = Math.min(
          delayWithoutJitter + jitter,
          options.maxDelayMs,
        );

        if (isLegacy) {
          console.log(`Waiting ${Math.round(delayMs / 1000)}s before retry...`);
        } else {
          options.onRetry?.(attempt, normalizedError);
        }
        await delay(delayMs);
      }
    }

    throw (
      lastError ??
      new Error(
        isLegacy
          ? `${options.operationName} failed after ${options.maxAttempts} attempts`
          : 'Operation failed after retries',
      )
    );
  }
}
